// Main App Component with Router

import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MainLayout } from './components/layout';
import { ProtectedRoute } from './components/auth';
import CopilotRoute from './components/auth/CopilotRoute';
import { LoginPage, SetPasswordPage, ForgotPasswordPage, DashboardPage, RagAdminPage, AgentChatPage, AgentConsolePage, InfoUploadPage, WidgetManagementPage, WidgetTestPage, AlertsPage, GmailAdminPage, ApiKeysPage, SystemsPage, EmailMonitorDecisionsPage, OrdersPage, UserManagementPage, CplexAdminPage, PortalAnalyticsDashboard, InboxAnalyticsPage, CostsPage, ReadoutPage, CustomerGradingPage, EmailEvalPage, AgentEvalPage, QualityIssuesPage, BolReviewPage, SignalsReportsPage, SignalsReportViewPage, RoadmapReportsPage, FreightAuditPage, SurchargeReconciliationPage, SignalsThreadsPage, SignalsConstellationsPage, SignalsReviewPage, SignalsBankPage, BriefingPage, MerlinWatchesPage, QualityRoutingAdminPage, StaffAddressesPage, IntakeRoutingPage, AgentTopologyPage, KBEffectivenessPage, FastpathQualityPage, MerlinMonitorPage, KnowledgeFilerPage, QualityCopilotPage, TechSupportCopilotPage, AnalystCopilotPage, NotFoundPage } from './pages';
import MyRoadmapPage from './pages/portal/MyRoadmapPage';
import MyMeetingsPage from './pages/portal/MyMeetingsPage';
import QuestionsForMePage from './pages/portal/QuestionsForMePage';
import { CplexSankeyFullPage } from './pages/CplexSankeyFullPage';
import { CplexFillPlanPage } from './pages/CplexFillPlanPage';
import { PortalLoginPage, PortalInboxPage, AllEmailsPage, SentPage, PortalOrdersPage, PortalTeamPage, PortalAnalyticsPage, PortalCustomersPage, PortalCustomerDetailPage, PortalSettingsPage, PortalMySalespeoplePage, PortalPricingExemptionsPage, PortalKnowledgeBanksPage, PortalMissedOrdersPage } from './pages/portal';
import PortalLayout from './components/portal/PortalLayout';
import QualityTriageShell from './pages/quality-triage/QualityTriageShell';
import QualityTriagePage from './pages/quality-triage/QualityTriagePage';
import QualityCasesPage from './pages/quality-triage/QualityCasesPage';
import QualityRoutedPage from './pages/quality-triage/QualityRoutedPage';
import QualityDonePage from './pages/quality-triage/QualityDonePage';
import QualityPendingPage from './pages/quality-triage/QualityPendingPage';
import QualityDraftsPage from './pages/quality-triage/QualityDraftsPage';
import WaitingRedirect from './pages/quality-triage/WaitingRedirect';
import {
  CaseWorkspacePage,
  ThreadWorkspacePage,
} from './pages/quality-triage/QualityWorkspacePage';
import { CopilotApp } from './copilot/CopilotApp';
import { BrainDumpWrapper } from './pages/BrainDumpWrapper';
import { AudioRecorderTestPage } from './copilot/components/__test__/AudioRecorderTestPage';
import { getDefaultLandingPage, isExecPortalUser } from './utils/landingPage';
import { EXEC_PORTAL_HOME } from './constants/execPortal';
import { OrdersManagerRoute } from './components/portal/OrdersManagerRoute';
import { AdminSignalsDeepLink } from './components/signals/AdminSignalsDeepLink';
import { AppErrorBoundary } from './components/common/AppErrorBoundary';
import './styles/global.css';

// Redirect to login or scope-appropriate landing page based on auth status
function HomeRedirect() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const destination = user ? getDefaultLandingPage(user) : '/dashboard';
  return <Navigate to={destination} replace />;
}

// Redirect /portal to the appropriate default page based on user scopes
function PortalIndexRedirect() {
  const { hasScope, user } = useAuth();
  // Executive readers (PJ / Ravi / Hadi) first: they may be ADMIN-role, so the
  // orders:manage short-circuit below would otherwise send them to the inbox.
  if (user && isExecPortalUser(user)) return <Navigate to={EXEC_PORTAL_HOME} replace />;
  if (hasScope('orders:manage')) return <Navigate to="/portal/inbox" replace />;
  // Quality-only staff (no orders:manage) land on the Co-Pilot rather than the
  // customers list. Admins have orders:manage, so they never reach this branch.
  if (hasScope('quality:copilot') || hasScope('quality:viewer')) return <Navigate to="/portal/quality-copilot" replace />;
  // Tech-support staff (John Kirk) land on the Tech Support Co-Pilot.
  if (hasScope('techsupport:copilot')) return <Navigate to="/portal/tech-support-copilot" replace />;
  // Quality Signals reader (Ravi Joshi) — no orders:manage, lands on the
  // signals reports feed rather than the customers list.
  if (hasScope('signals:read')) return <Navigate to="/portal/signals" replace />;
  return <Navigate to="/portal/customers" replace />;
}

// Gate the Orders page at orders:read — any Customers-tab persona can VIEW
// orders; edit affordances inside the page still require orders:manage.
function OrdersViewerRoute({ children }: { children: React.ReactNode }) {
  const { hasScope } = useAuth();
  if (!hasScope('orders:read')) {
    return <Navigate to="/portal/customers" replace />;
  }
  return <>{children}</>;
}

// Gate the portal Quality Signals reader area (Reports + Constellations) to
// holders of the signals:read grant (admins pass via hasScope). This mirrors
// the backend read-guard require_any_scope("signals:read"). Others are sent to
// their default portal page.
function SignalsPortalRoute({ children }: { children: React.ReactNode }) {
  const { hasScope } = useAuth();
  if (!hasScope('signals:read')) {
    return <Navigate to="/portal/customers" replace />;
  }
  return <>{children}</>;
}


// The Quality Triage shell's index route: honour the door's legacy deep links
// before landing on the queue. `?conversation=` predates the shell (bell items,
// bookmarks) and belongs to the chat tab; `?investigationId=` was written by
// older investigation notifications — since Phase 2 a case has a real URL
// (cases/<id>), so the old link lands on that case's workspace.
function QcpLegacyIndex() {
  const [params] = useSearchParams();
  const conversation = params.get('conversation');
  if (conversation) {
    return <Navigate to={`ask?conversation=${encodeURIComponent(conversation)}`} replace />;
  }
  const investigationId = params.get('investigationId');
  if (investigationId) {
    return <Navigate to={`cases/${encodeURIComponent(investigationId)}`} replace />;
  }
  // The bell's "Merlin: answered on QT-10057" row links the door with
  // ?thread=<thread id> (services/merlin_followup/delivery.py); the thread
  // workspace resolves ids as well as QT numbers (kanban 43160d8b).
  const threadId = params.get('thread');
  if (threadId) {
    return <Navigate to={`threads/${encodeURIComponent(threadId)}`} replace />;
  }
  return <QualityTriagePage />;
}

// Gate John Kirk's Tech Support Co-Pilot to staff with the techsupport:copilot
// grant OR any admin (mirrors the backend `require_tech_support_user`, which
// lets admins through — admin role defaults do NOT include techsupport:copilot).
// Others are sent to their default landing page.
function TechSupportRoute({ children }: { children: React.ReactNode }) {
  const { hasScope, user } = useAuth();
  const allowed = hasScope('techsupport:copilot') || user?.role === 'ADMIN';
  if (!allowed) {
    return <Navigate to={user ? getDefaultLandingPage(user) : '/dashboard'} replace />;
  }
  return <>{children}</>;
}

// Gate the Analyst door to staff with the analyst:copilot grant OR any admin
// (mirrors the backend `require_analyst_user`, which lets admins through — admin
// role defaults do NOT include analyst:copilot). Others are sent to their
// default landing page.
function AnalystRoute({ children }: { children: React.ReactNode }) {
  const { hasScope, user } = useAuth();
  const allowed = hasScope('analyst:copilot') || user?.role === 'ADMIN';
  if (!allowed) {
    return <Navigate to={user ? getDefaultLandingPage(user) : '/dashboard'} replace />;
  }
  return <>{children}</>;
}

// Gate the executive portal tabs (Roadmap, Quality Issues, Customer Grading,
// Costs, Portal Analytics) to holders of the exec:portal grant OR any admin
// (mirrors the backend require_any_scope("exec:portal") read guards, which let
// admins through). Others go to their landing page.
function ExecRoute({ children }: { children: React.ReactNode }) {
  const { hasScope, user } = useAuth();
  const allowed = hasScope('exec:portal') || user?.role === 'ADMIN';
  if (!allowed) {
    return <Navigate to={user ? getDefaultLandingPage(user) : '/dashboard'} replace />;
  }
  return <>{children}</>;
}

// Gate the portal Knowledge Banks tab (the hosted KB Explorer in an iframe) to
// holders of the kb:explore grant OR any admin — the same rule the backend
// applies to /kb-explorer/ itself (app/routes/kb_explorer.py may_explore).
function KbExploreRoute({ children }: { children: React.ReactNode }) {
  const { hasScope, user } = useAuth();
  const allowed = hasScope('kb:explore') || user?.role === 'ADMIN';
  if (!allowed) {
    return <Navigate to={user ? getDefaultLandingPage(user) : '/dashboard'} replace />;
  }
  return <>{children}</>;
}

// Gate the portal Inbox Analytics tab to staff with the analytics:view grant OR
// any admin (mirrors the broadened `/api/stats/inbox-response-times` endpoint,
// which admits admins + the analytics:view scope). Others go to their landing.
function AnalyticsRoute({ children }: { children: React.ReactNode }) {
  const { hasScope, user } = useAuth();
  const allowed = hasScope('analytics:view') || user?.role === 'ADMIN';
  if (!allowed) {
    return <Navigate to={user ? getDefaultLandingPage(user) : '/dashboard'} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    // Last line of defence (kanban 90f3b9d8): any render error that escapes
    // the page/section boundaries inside the layouts lands here instead of
    // unmounting the React root into a blank white page. Sits OUTSIDE the
    // router on purpose so it still renders if the router itself throws.
    <AppErrorBoundary scope="app" homeHref="/">
    <BrowserRouter>
      <Routes>
        {/* Sales Copilot — mobile PWA, standalone auth */}
        <Route path="/copilot/*" element={<CopilotApp />} />

        {/* Brain Dump - standalone endpoint with its own auth (no AuthProvider needed) */}
        <Route path="/dump" element={<BrainDumpWrapper />} />

        {/* Dev test routes - standalone, no auth */}
        <Route path="/test/audio-recorder" element={<AudioRecorderTestPage />} />

        {/* All other routes wrapped in AuthProvider */}
        <Route path="/*" element={<AuthenticatedApp />} />
      </Routes>
    </BrowserRouter>
    </AppErrorBoundary>
  );
}

function AuthenticatedApp() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public login page - no layout */}
        <Route path="/login" element={<LoginPage />} />
        {/* Password links (kanban c369e94b): public, token-gated / no account discovery */}
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Root redirects based on auth status */}
          <Route path="/" element={<HomeRedirect />} />

          {/* Main layout routes. `adminOnly` marks the admin-panel pages
              (kanban 690c6e54): a non-admin who deep-links to one is sent to
              their own landing page instead of rendering the admin shell,
              which would 403 on every /api/admin call. Routes with an explicit
              scope story (orders:manage, grading:review, the /signals reader
              deep links) keep their own guards. */}
          <Route element={<MainLayout />}>
            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />

            {/* RAG Admin */}
            <Route
              path="/rag"
              element={
                <ProtectedRoute adminOnly>
                  <RagAdminPage />
                </ProtectedRoute>
              }
            />

            {/* Agent Chat */}
            <Route
              path="/agent"
              element={
                <ProtectedRoute>
                  <AgentChatPage />
                </ProtectedRoute>
              }
            />

            {/* Agent Console is a STANDALONE full-screen route (outside
                MainLayout) — see below, after the layout wrapper closes. */}

            {/* Info Upload */}
            <Route
              path="/info"
              element={
                <ProtectedRoute adminOnly>
                  <InfoUploadPage />
                </ProtectedRoute>
              }
            />

            {/* Widget Management */}
            <Route
              path="/widgets"
              element={
                <ProtectedRoute adminOnly>
                  <WidgetManagementPage />
                </ProtectedRoute>
              }
            />

            {/* Widget Test Page */}
            <Route
              path="/widget-test/:widgetId"
              element={
                <ProtectedRoute adminOnly>
                  <WidgetTestPage />
                </ProtectedRoute>
              }
            />

            {/* Alerts / Red Flags */}
            <Route
              path="/alerts"
              element={
                <ProtectedRoute>
                  <AlertsPage />
                </ProtectedRoute>
              }
            />

            {/* Email Admin (Gmail + Outlook) */}
            <Route
              path="/gmail"
              element={
                <ProtectedRoute adminOnly>
                  <GmailAdminPage />
                </ProtectedRoute>
              }
            />
            {/* OAuth callback redirect lands here */}
            <Route
              path="/admin/email"
              element={
                <ProtectedRoute adminOnly>
                  <GmailAdminPage />
                </ProtectedRoute>
              }
            />

            {/* Email Monitor (unified pipeline page) */}
            <Route
              path="/email-monitor-decisions"
              element={
                <ProtectedRoute adminOnly>
                  <EmailMonitorDecisionsPage />
                </ProtectedRoute>
              }
            />

            {/* The live agent / Knowledge-Bank graph, derived from the registries */}
            <Route
              path="/agent-topology"
              element={
                <ProtectedRoute adminOnly>
                  <AgentTopologyPage />
                </ProtectedRoute>
              }
            />

            {/* Merlin Visibility Pane — every Merlin conversation across every
                stream (admin-only; the API 403s non-admins). */}
            <Route
              path="/merlin-monitor"
              element={
                <ProtectedRoute adminOnly>
                  <MerlinMonitorPage />
                </ProtectedRoute>
              }
            />

            {/* Inbox → extraction-pipeline routing (visual flowchart editor) */}
            <Route
              path="/intake-routing"
              element={
                <ProtectedRoute adminOnly>
                  <IntakeRoutingPage />
                </ProtectedRoute>
              }
            />

            {/* Knowledge Filer — the nightly filer's runs, writes, cost, ledger */}
            <Route
              path="/knowledge-filer"
              element={
                <ProtectedRoute adminOnly>
                  <KnowledgeFilerPage />
                </ProtectedRoute>
              }
            />

            {/* Email Eval Harness (admin) */}
            <Route
              path="/email-eval"
              element={
                <ProtectedRoute adminOnly>
                  <EmailEvalPage />
                </ProtectedRoute>
              }
            />

            {/* Knowledge-Bank effectiveness audit — weekly scorecards (admin) */}
            <Route
              path="/kb-effectiveness"
              element={
                <ProtectedRoute>
                  <KBEffectivenessPage />
                </ProtectedRoute>
              }
            />

            {/* Fast path quality — truth-set eval runs + watchdog checks (admin) */}
            <Route
              path="/fastpath-quality"
              element={
                <ProtectedRoute adminOnly>
                  <FastpathQualityPage />
                </ProtectedRoute>
              }
            />

            {/* Generalized Agent Eval (admin) */}
            <Route
              path="/agent-eval"
              element={
                <ProtectedRoute adminOnly>
                  <AgentEvalPage />
                </ProtectedRoute>
              }
            />

            {/* BOL review queue — human linking for unresolved scans */}
            <Route
              path="/bol-review"
              element={
                <ProtectedRoute adminOnly>
                  <BolReviewPage />
                </ProtectedRoute>
              }
            />

            {/* Quality Issues triage (QCP, admin) */}
            <Route
              path="/quality-issues"
              element={
                <ProtectedRoute adminOnly>
                  <QualityIssuesPage />
                </ProtectedRoute>
              }
            />

            {/* Quality Routing — Scott's screen over Merlin's tracked threads */}
            <Route
              path="/quality-routing"
              element={
                <ProtectedRoute adminOnly>
                  <QualityRoutingAdminPage />
                </ProtectedRoute>
              }
            />

            {/* Staff addresses — who really uses which e-mail address (kanban ba007a0f) */}
            <Route
              path="/staff-addresses"
              element={
                <ProtectedRoute adminOnly>
                  <StaffAddressesPage />
                </ProtectedRoute>
              }
            />

            {/* Quality Signals reports feed (agent-authored reports) */}
            <Route
              path="/signals"
              element={
                <ProtectedRoute>
                  <AdminSignalsDeepLink portalPath="/portal/signals">
                    <SignalsReportsPage />
                  </AdminSignalsDeepLink>
                </ProtectedRoute>
              }
            />

            {/* Freight billing audit — on-demand, read-only reconstruction */}
            <Route
              path="/freight-audit"
              element={
                <ProtectedRoute requiredScope="orders:manage">
                  <FreightAuditPage />
                </ProtectedRoute>
              }
            />
            {/* Surcharge reconciliation — the weekly expected-vs-actual run, on screen */}
            <Route
              path="/surcharge-reconciliation"
              element={
                <ProtectedRoute requiredScope="orders:manage">
                  <SurchargeReconciliationPage />
                </ProtectedRoute>
              }
            />
            {/* Initiative Roadmap reports feed (BETA — task-force roadmap agent) */}
            <Route
              path="/roadmap"
              element={
                <ProtectedRoute adminOnly>
                  <RoadmapReportsPage />
                </ProtectedRoute>
              }
            />

            {/* Quality Signals — Common Threads explorer (beta) */}
            <Route
              path="/signals/threads"
              element={
                <ProtectedRoute adminOnly>
                  <SignalsThreadsPage />
                </ProtectedRoute>
              }
            />

            {/* Quality Signals — Constellations (semantic clustering) */}
            <Route
              path="/signals/constellations"
              element={
                <ProtectedRoute>
                  <AdminSignalsDeepLink portalPath="/portal/signals/constellations">
                    <SignalsConstellationsPage />
                  </AdminSignalsDeepLink>
                </ProtectedRoute>
              }
            />

            {/* Quality Signals — learning-loop review queue */}
            <Route
              path="/signals/review"
              element={
                <ProtectedRoute adminOnly>
                  <SignalsReviewPage />
                </ProtectedRoute>
              }
            />

            {/* Quality Signals — bank health (writer agent + platform dedup) */}
            <Route
              path="/signals/bank"
              element={
                <ProtectedRoute adminOnly>
                  <SignalsBankPage />
                </ProtectedRoute>
              }
            />

            {/* Merlin watches — "tell Merlin what to watch" (kanban 8e4296bc).
                Mirrors /portal/watches; admins may view everyone's. */}
            <Route
              path="/watches"
              element={
                <ProtectedRoute>
                  <MerlinWatchesPage />
                </ProtectedRoute>
              }
            />
            {/* The Briefing page — the reader's watches, their daily briefing
                first (kanban 001b6e2b folded the personal digest into watches;
                the /digest path is kept so links and the usage graph carry on).
                Mirrors /portal/signals/digest. */}
            <Route
              path="/signals/digest"
              element={
                <ProtectedRoute>
                  <AdminSignalsDeepLink portalPath="/portal/signals/digest">
                    <BriefingPage />
                  </AdminSignalsDeepLink>
                </ProtectedRoute>
              }
            />

            {/* API Keys Management */}
            <Route
              path="/api-keys"
              element={
                <ProtectedRoute adminOnly>
                  <ApiKeysPage />
                </ProtectedRoute>
              }
            />

            {/* Systems Exploration */}
            <Route
              path="/systems"
              element={
                <ProtectedRoute>
                  <SystemsPage />
                </ProtectedRoute>
              }
            />

            {/* Orders (Admin) */}
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <OrdersPage />
                </ProtectedRoute>
              }
            />

            {/* User Management (Admin) */}
            <Route
              path="/users"
              element={
                <ProtectedRoute adminOnly>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />

            {/* CPLEX Admin Runner (Admin) */}
            <Route
              path="/cplex"
              element={
                <ProtectedRoute requiredScope="admin:system">
                  <CplexAdminPage />
                </ProtectedRoute>
              }
            />

            {/* Portal Analytics (Admin) */}
            <Route
              path="/portal-analytics"
              element={
                <ProtectedRoute adminOnly>
                  <PortalAnalyticsDashboard />
                </ProtectedRoute>
              }
            />

            {/* Inbox Response Analytics (Admin) */}
            <Route
              path="/inbox-analytics"
              element={
                <ProtectedRoute adminOnly>
                  <InboxAnalyticsPage />
                </ProtectedRoute>
              }
            />

            {/* Costs — infra + Agent Console spend (Admin) */}
            <Route
              path="/costs"
              element={
                <ProtectedRoute adminOnly>
                  <CostsPage />
                </ProtectedRoute>
              }
            />

            {/* Readout — every system at a glance: narrative, live numbers,
                cost / funding / benefit (Admin; exec:portal readers get the
                portal mount below). */}
            <Route
              path="/readout"
              element={
                <ProtectedRoute adminOnly>
                  <ReadoutPage />
                </ProtectedRoute>
              }
            />

            {/* Customer A–F Grading — run controls + runs history (Admin /
                grading:review holders). The daily cron is off by default. */}
            <Route
              path="/customer-grading"
              element={
                <ProtectedRoute>
                  <CustomerGradingPage />
                </ProtectedRoute>
              }
            />

          </Route>

          {/* Agent Console — standalone full-screen (NOT wrapped in MainLayout
              so it gets the whole viewport; the nav opens it in its own tab).
              The page renders its own sidebar + docked panels. Scope-gated. */}
          <Route
            path="/agent-console"
            element={
              <ProtectedRoute requiredScope="agent:console">
                <AgentConsolePage />
              </ProtectedRoute>
            }
          />

          {/* CPLEX Production Fill Plan (Admin) — PJ's table-first landing
              for the production planner (kanban bb4492c8): finished goods ×
              day 1..5 fill + chat; the Sankey is the secondary view behind a
              button. Standalone, NOT wrapped in MainLayout. */}
          <Route
            path="/cplex/fill-plan"
            element={
              <ProtectedRoute requiredScope="admin:system">
                <CplexFillPlanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cplex/fill-plan/:runId"
            element={
              <ProtectedRoute requiredScope="admin:system">
                <CplexFillPlanPage />
              </ProtectedRoute>
            }
          />

          {/* CPLEX Sankey full-bleed view (Admin) — truly standalone, NOT
              wrapped in MainLayout so it gets the whole viewport. The page
              itself renders its own toolbar / chips / floating panels. */}
          <Route
            path="/cplex/sankey"
            element={
              <ProtectedRoute requiredScope="admin:system">
                <CplexSankeyFullPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cplex/sankey/:runId"
            element={
              <ProtectedRoute requiredScope="admin:system">
                <CplexSankeyFullPage />
              </ProtectedRoute>
            }
          />

          {/* Quality Signals — standalone report viewer (opened in a new tab
              from the reports feed). NOT wrapped in MainLayout so the report
              fills the whole viewport, matching the agent's standalone page. */}
          <Route
            path="/signals/reports/:id"
            element={
              <ProtectedRoute>
                <SignalsReportViewPage />
              </ProtectedRoute>
            }
          />

          {/* Sales Portal - separate layout for customer-facing portal */}
          <Route path="/portal/login" element={<PortalLoginPage />} />
          <Route
            path="/portal"
            element={
              <ProtectedRoute>
                <PortalLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<PortalIndexRedirect />} />
            <Route path="inbox" element={<OrdersManagerRoute><PortalInboxPage /></OrdersManagerRoute>} />
            <Route path="sent" element={<OrdersManagerRoute><SentPage /></OrdersManagerRoute>} />
            <Route path="all-emails" element={<OrdersManagerRoute><AllEmailsPage /></OrdersManagerRoute>} />
            <Route path="orders" element={<OrdersViewerRoute><PortalOrdersPage /></OrdersViewerRoute>} />
            {/* Standing per-ship-to pricing exemptions — read-only reference
                for CSRs (e.g. White Cap Lansing MI prices from ERSCO02).
                Viewer-gated to match the Orders tab. */}
            <Route path="pricing-exemptions" element={<OrdersViewerRoute><PortalPricingExemptionsPage /></OrdersViewerRoute>} />
            {/* Missed Orders — Merlin's per-order verdicts beside PJ's grade with the
                one-click "that is wrong, it was X because Y" (kanban fc6bca28).
                Viewer-gated like the Orders tab (orders:read; the API enforces it). */}
            <Route path="missed-orders" element={<OrdersViewerRoute><PortalMissedOrdersPage /></OrdersViewerRoute>} />
            <Route path="customers" element={<PortalCustomersPage />} />
            <Route path="customers/:slug" element={<PortalCustomerDetailPage />} />
            {/* Analyst — the read-only data Analyst behind a profile-LOCKED door
                (the SAME `analyst` agent as the console, focused portal UI).
                Full-bleed chat below the shared portal navbar. */}
            <Route path="analyst" element={<AnalystRoute><AnalystCopilotPage /></AnalystRoute>} />
            {/* Inbox Analytics — the SAME response-time panel as the admin
                dashboard, reused for finance/ops staff (analytics:view). */}
            <Route path="inbox-analytics" element={<AnalyticsRoute><InboxAnalyticsPage /></AnalyticsRoute>} />
            {/* Knowledge Banks — the gurucloud-kb Knowledge Bank Explorer the api
                hosts at /kb-explorer/, embedded full-bleed in the portal shell.
                Gated by the kb:explore grant (admins pass); the api enforces
                the same rule on the framed page and its API. */}
            <Route path="knowledge-banks" element={<KbExploreRoute><PortalKnowledgeBanksPage /></KbExploreRoute>} />
            {/* Quality Triage — the reimagined Quality Co-Pilot door (review
                3ofroc): triage-first tabs under one light shell. Gated by the
                quality:copilot grant (admins pass). The chat lives at
                `ask` (the bar's Ask Merlin button); the index honours the old ?conversation= deep links. */}
            <Route path="quality-copilot" element={<CopilotRoute><QualityTriageShell /></CopilotRoute>}>
              <Route index element={<QcpLegacyIndex />} />
              <Route path="cases" element={<QualityCasesPage />} />
              {/* Phase 2 split workspace — every row / card lands at a real
                  URL (thread QT-# or case INV-#, ids accepted too). */}
              <Route path="cases/:caseRef" element={<CaseWorkspacePage />} />
              <Route path="threads/:threadRef" element={<ThreadWorkspacePage />} />
              {/* Items parked on a named person had their own tab in the tabs
                  round; they are a filter on Triage now, so old links land
                  there with the filter on. */}
              <Route path="waiting" element={<WaitingRedirect />} />
              <Route path="routed" element={<QualityRoutedPage />} />
              {/* Every question Merlin drafted and nobody sent yet, across
                  items (UX round 2026-10-20), under More. */}
              <Route path="drafts" element={<QualityDraftsPage />} />
              {/* Closed threads (Done / Dismissed) — their own tab since 2026-09-08. */}
              <Route path="done" element={<QualityDonePage />} />
              {/* Everything Jess asked Merlin to watch or come back on, with
                  the answers waiting to be read (kanban 43160d8b). */}
              <Route path="pending" element={<QualityPendingPage />} />
              {/* Same reports feed as /portal/signals, folded into the door so
                  quality has one home (signals:read still gates the data). */}
              <Route path="briefs" element={<SignalsPortalRoute><SignalsReportsPage variant="portal" /></SignalsPortalRoute>} />
              {/* Its Constellations / My watches sub-tabs stay inside the door,
                  with the Quality tabs still showing (fan-out finding 4). */}
              <Route path="briefs/constellations" element={<SignalsPortalRoute><SignalsConstellationsPage variant="portal" /></SignalsPortalRoute>} />
              <Route path="briefs/digest" element={<SignalsPortalRoute><BriefingPage variant="portal" /></SignalsPortalRoute>} />
              {/* General Merlin chat: no longer a tab (tabs round) — reached from
                  the bar's "Ask Merlin" button and "+ New quality issue"; the
                  route and the ?conversation= links into it stay. */}
              <Route path="ask" element={<QualityCopilotPage embedded />} />
            </Route>
            {/* Tech Support Co-Pilot — John Kirk's chat-only portal (email tool
                deferred); gated by the techsupport:copilot grant (admins pass).
                Full-bleed below the shared portal navbar. */}
            <Route path="tech-support-copilot" element={<TechSupportRoute><TechSupportCopilotPage /></TechSupportRoute>} />
            {/* Quality Signals reader area — the SAME Reports feed +
                Constellations explorer as the admin /signals pages, rendered in
                the portal shell for a non-admin exec (Ravi). Gated by the
                signals:read grant (admins pass). Report generation and the
                Common Threads / Needs review tabs stay admin-only. */}
            <Route path="signals" element={<SignalsPortalRoute><SignalsReportsPage variant="portal" /></SignalsPortalRoute>} />
            <Route path="signals/constellations" element={<SignalsPortalRoute><SignalsConstellationsPage variant="portal" /></SignalsPortalRoute>} />
            {/* The Briefing page — the reader's watches (daily briefing first)
                and the chat that changes them (signals:read; the chat door
                checks the same grant server-side). */}
            <Route path="signals/digest" element={<SignalsPortalRoute><BriefingPage variant="portal" /></SignalsPortalRoute>} />
            {/* Executive portal (PJ / Ravi — kanban f9aec0be): the SAME
                pages the admin panel serves, rendered in the portal shell and
                gated by exec:portal (admins pass). Every write / run control
                inside them is hidden for non-admins, and the backend keeps
                those endpoints admin-only. See constants/execPortal.ts. */}
            <Route path="roadmap" element={<ExecRoute><RoadmapReportsPage /></ExecRoute>} />
            {/* Merlin watches live on the Briefing page beside the one chat that
                changes them (kanban 14927fad, 001b6e2b); the old route redirects. */}
            <Route path="watches" element={<Navigate to="/portal/signals/digest" replace />} />
            {/* A portal user's own slice of the roadmap (kanban 5895aca3): open to
                every signed-in portal user; the API scopes it to them. */}
            <Route path="my-roadmap" element={<MyRoadmapPage />} />
            {/* The agenda for the meetings this person is in (kanban 108573ba):
                open to every signed-in portal user; the API scopes the series. */}
            <Route path="meetings" element={<MyMeetingsPage />} />
            {/* The quality desk's open asks for this person (kanban 2a28d4c0). */}
            <Route path="questions-for-me" element={<QuestionsForMePage />} />
            <Route path="quality-issues" element={<ExecRoute><QualityIssuesPage /></ExecRoute>} />
            <Route path="customer-grading" element={<ExecRoute><CustomerGradingPage /></ExecRoute>} />
            <Route path="costs" element={<ExecRoute><CostsPage /></ExecRoute>} />
            <Route path="readout" element={<ExecRoute><ReadoutPage /></ExecRoute>} />
            <Route path="portal-analytics" element={<ExecRoute><PortalAnalyticsDashboard /></ExecRoute>} />
            <Route path="my-salespeople" element={<OrdersManagerRoute><PortalMySalespeoplePage /></OrdersManagerRoute>} />
            <Route path="team" element={<OrdersManagerRoute><PortalTeamPage /></OrdersManagerRoute>} />
            <Route path="analytics" element={<OrdersManagerRoute><PortalAnalyticsPage /></OrdersManagerRoute>} />
            <Route path="settings" element={<PortalSettingsPage />} />
            {/* Unknown /portal/* sub-route: render a 404 inside the portal
                shell. Unauthenticated users are redirected to /login by the
                parent ProtectedRoute before reaching this. Previously these
                URLs (e.g. /portal/orders-inbox) rendered a blank page. */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Global catch-all: any URL that matches no route above. Wrapping
              in ProtectedRoute preserves the app-wide guarantee that
              unauthenticated users are redirected to /login (returnUrl
              preserved) rather than shown a blank page; authenticated users
              get a 404 instead of an empty DOM. */}
          <Route path="*" element={<ProtectedRoute><NotFoundPage /></ProtectedRoute>} />
        </Routes>
      </AuthProvider>
  );
}
